import { useEffect, useState } from "react";
import { fetchTemplates } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, FolderOpen, Lock } from "lucide-react";

export default function TemplatePicker({ open, onClose, calculator, onApply }) {
  const { isAuthed, hasPro, setShowAuth } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !isAuthed || !hasPro) return;
    setLoading(true);
    fetchTemplates(calculator)
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, isAuthed, hasPro, calculator]);

  if (!open) return null;

  return (
    <div
      data-testid="template-picker"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[80vh] flex flex-col border border-[#334155] bg-[#11151c]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#334155]">
          <div className="flex items-center gap-2 text-xs mono tracking-widest text-[#f59e0b]">
            <FolderOpen className="w-4 h-4" />
            RIGCALC://TEMPLATES · {calculator}
          </div>
          <button data-testid="tpl-close" onClick={onClose} className="text-[#94a3b8] hover:text-[#f8fafc]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!isAuthed ? (
            <div className="text-center py-6">
              <Lock className="w-6 h-6 text-[#f59e0b] mx-auto mb-2" />
              <p className="mono text-sm text-[#94a3b8] mb-3">Sign in to load templates.</p>
              <button
                onClick={() => { onClose(); setShowAuth(true); }}
                className="bg-[#f59e0b] text-[#0b0e13] px-3 py-2 mono text-xs tracking-widest uppercase font-bold"
              >
                Sign In
              </button>
            </div>
          ) : !hasPro ? (
            <div className="text-center py-6 mono text-sm text-[#94a3b8]">
              Pro plan required to save & load templates.
            </div>
          ) : loading ? (
            <div className="mono text-sm text-[#94a3b8]">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="mono text-sm text-[#94a3b8] py-6 text-center">
              No templates yet for <span className="text-[#f59e0b]">{calculator}</span>.
              <br />
              <span className="text-[10px] text-[#64748b]">
                Save a calculation from Saved Jobs and promote it to a template.
              </span>
            </div>
          ) : (
            <ul className="space-y-2">
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    data-testid={`tpl-apply-${t.id}`}
                    onClick={() => { onApply(t); onClose(); }}
                    className="w-full text-left border border-[#334155] hover:border-[#f59e0b] bg-[#0b0e13] p-3 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="mono text-sm text-[#f8fafc] group-hover:text-[#f59e0b]">{t.name}</span>
                      <span className="text-[10px] mono text-[#64748b]">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    {(t.rig_name || t.well_name) && (
                      <div className="mt-1 text-[10px] mono text-[#94a3b8]">
                        {t.rig_name && <span>RIG: <span className="text-[#f59e0b]">{t.rig_name}</span></span>}
                        {t.rig_name && t.well_name && " · "}
                        {t.well_name && <span>WELL: <span className="text-[#f59e0b]">{t.well_name}</span></span>}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
